import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import U2000BasicDialog from '../../../u2000Core/components/dialog/U2000BasicDialog';
import { FaPlus, FaUndoAlt } from 'react-icons/fa';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { NS } from '../../U3208';
import {
  u3208IsAllowedUploadFile,
  u3208MimeTypesToAccept,
  type U3208AllowedMimeType,
  type U3208DocSource,
  type U3208U2200Job,
} from '../../U3208Service';

const cancelButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs transition-all duration-200
  hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
  active:bg-gray-100 active:scale-95
  disabled:cursor-not-allowed disabled:opacity-60`;
const primaryButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg shadow-xs transition-all duration-200
  hover:bg-green-700 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2
  active:bg-green-800 active:scale-95
  disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200 disabled:opacity-60 disabled:shadow-none`;

export interface U3208UploadSourceDialogProps {
  isOpen: boolean;
  allowedMimeTypes: U3208AllowedMimeType[];
  maxFileSizeBytes: number;
  submitting?: boolean;
  onClose: () => void;
  onUpload: (formData: FormData) => Promise<{ source: U3208DocSource; job: U3208U2200Job } | null>;
  onUploaded: (result: { source: U3208DocSource; job: U3208U2200Job }) => void;
}

const U3208UploadSourceDialog: React.FC<U3208UploadSourceDialogProps> = ({
  isOpen,
  allowedMimeTypes,
  maxFileSizeBytes,
  submitting = false,
  onClose,
  onUpload,
  onUploaded,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const [formResetKey, setFormResetKey] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setLocalSubmitting(false);
      setFormResetKey((k) => k + 1);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [isOpen]);

  const busy = submitting || localSubmitting;
  const accept = u3208MimeTypesToAccept(allowedMimeTypes);

  const handleSubmit = async () => {
    if (!file) {
      toast.error(t('error.missingFile', { ns: NS }));
      return;
    }
    const check = u3208IsAllowedUploadFile(file, allowedMimeTypes, maxFileSizeBytes);
    if (check === 'tooLarge') {
      toast.error(t('error.fileTooLarge', { ns: NS }));
      return;
    }
    if (check === 'unsupported') {
      toast.error(t('error.unsupportedType', { ns: NS }));
      return;
    }
    setLocalSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await onUpload(formData);
      if (result) {
        toast.success(t('toast.uploadOk', { ns: NS }));
        onUploaded(result);
        onClose();
      }
    } finally {
      setLocalSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('action.upload', { ns: NS })}
      width="medium"
      height="max-h-[90vh]"
    >
      <div className="flex h-full flex-col focus:outline-hidden">
        <section className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="u3208-upload-file">
            {t('lblUploadFile', { ns: NS })}
          </label>
          <p className="mb-2 text-xs text-gray-500">{t('hintUploadFormats', { ns: NS })}</p>
          <div className="flex flex-col gap-1">
            <input
              id="u3208-upload-file"
              ref={inputRef}
              key={formResetKey}
              type="file"
              accept={accept}
              disabled={busy}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-700 file:mr-3 file:rounded-sm file:border-0 file:bg-gray-100 file:px-3 file:py-1.5"
            />
            {file ? (
              <button
                type="button"
                className="self-start text-xs text-gray-600 underline"
                onClick={() => {
                  setFile(null);
                  if (inputRef.current) inputRef.current.value = '';
                }}
                disabled={busy}
              >
                {t('btnClearFile', { ns: NS })}
              </button>
            ) : null}
          </div>
        </section>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <button type="button" className={cancelButtonClass} onClick={onClose} disabled={busy}>
            <FaUndoAlt className="h-4 w-4" />
            {t('btnCancel', { ns: NS })}
          </button>
          <button
            type="button"
            className={primaryButtonClass}
            onClick={() => void handleSubmit()}
            disabled={busy || !file}
          >
            <FaPlus className="h-4 w-4" />
            {t('action.upload', { ns: NS })}
          </button>
        </div>
      </div>
    </U2000BasicDialog>
  );
};

export default U3208UploadSourceDialog;
